<template>
  <span class="panel-header-title" ref="trigger"
    @mouseenter="startHover" @mouseleave="endHover">
    <span class="title-text">{{ title }}</span>
    <vue-feather type="chevron-down" size="14" class="title-chevron" />
    <slot name="subtitle" />
    <FloatingDropdown :open="isPopupOpen" :triggerRef="$refs.trigger" :matchWidth="false" :selectable="true"
      @dismiss="close">
      <div class="title-popup" ref="popup" @mouseenter="enterPopup" @mouseleave="leavePopup"
        @mousedown="handlePopupMousedown" @click="handlePopupClick">
        <slot />
        <div v-if="$slots.action" class="popup-action-footer" @click="close()">
          <slot name="action" />
        </div>
      </div>
    </FloatingDropdown>
  </span>
</template>

<script>
import { FloatingDropdown } from '@/design'
import VueFeather from 'vue-feather'

export default {
  name: 'PanelHeaderTitle',
  components: { FloatingDropdown, VueFeather },
  props: {
    title: { type: String, required: true }
  },
  data() {
    return {
      isPopupOpen: false,
      hoverTimeout: null,
      leaveTimeout: null,
      insidePopup: false,
      selectingInPopup: false
    }
  },
  methods: {
    startHover() {
      clearTimeout(this.leaveTimeout);
      if (this.isPopupOpen) return;
      this.hoverTimeout = setTimeout(() => {
        this.isPopupOpen = true;
      }, 200);
    },
    endHover() {
      clearTimeout(this.hoverTimeout);
      if (this.selectingInPopup) return;
      this.leaveTimeout = setTimeout(() => {
        if (!this.insidePopup) {
          this.isPopupOpen = false;
        }
      }, 150);
    },
    enterPopup() {
      clearTimeout(this.leaveTimeout);
      this.insidePopup = true;
      document.removeEventListener('mousedown', this.handleOutsideMousedown);
    },
    leavePopup() {
      this.insidePopup = false;
      // A text-selection drag that started in the popup may briefly overshoot
      // its edge — closing here would destroy the selection mid-drag
      if (this.selectingInPopup) return;
      this.leaveTimeout = setTimeout(() => {
        this.isPopupOpen = false;
      }, 150);
    },
    handlePopupMousedown() {
      this.selectingInPopup = true;
      document.removeEventListener('mousedown', this.handleOutsideMousedown);
      window.addEventListener('mouseup', this.handlePopupMouseup, { once: true });
    },
    handlePopupMouseup() {
      this.selectingInPopup = false;
      if (this.insidePopup) return;
      // Mouse released outside the popup. If the drag left a text selection
      // behind, keep the popup open so it can still be copied — it then
      // dismisses on the next click anywhere outside. Otherwise close as usual.
      const selection = window.getSelection();
      const popup = this.$refs.popup;
      if (popup && selection && !selection.isCollapsed && popup.contains(selection.anchorNode)) {
        document.addEventListener('mousedown', this.handleOutsideMousedown);
      } else {
        this.leaveTimeout = setTimeout(() => {
          this.isPopupOpen = false;
        }, 150);
      }
    },
    handleOutsideMousedown(event) {
      if (this.$refs.trigger && this.$refs.trigger.contains(event.target)) return;
      this.close();
    },
    handlePopupClick(event) {
      if (event.target.closest('.popup-action')) {
        this.close();
      }
    },
    close() {
      clearTimeout(this.hoverTimeout);
      clearTimeout(this.leaveTimeout);
      window.removeEventListener('mouseup', this.handlePopupMouseup);
      document.removeEventListener('mousedown', this.handleOutsideMousedown);
      this.selectingInPopup = false;
      this.insidePopup = false;
      this.isPopupOpen = false;
    }
  },
  unmounted() {
    clearTimeout(this.hoverTimeout);
    clearTimeout(this.leaveTimeout);
    window.removeEventListener('mouseup', this.handlePopupMouseup);
    document.removeEventListener('mousedown', this.handleOutsideMousedown);
  }
}
</script>

<style scoped>
.panel-header-title {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.panel-header-title:hover {
  background: #f3f4f6;
}

.title-text {
  font-weight: 700;
  font-size: 1rem;
  white-space: nowrap;
}

.title-chevron {
  color: #9ca3af;
  flex-shrink: 0;
  transition: transform 0.15s ease;
}

.panel-header-title:hover .title-chevron {
  color: #6b7280;
}
</style>

<style>
/* Popup menu — not scoped so it applies when teleported via FloatingDropdown */
.title-popup {
  background: #FFF;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.05);
  border-radius: 6px;
  min-width: 180px;
  max-width: 400px;
  padding: 8px;
}

.title-popup .popup-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 4px;
  font-size: 0.875rem;
  color: #374151;
  white-space: nowrap;
}

.title-popup .popup-action-footer {
  margin-top: 4px;
  padding-top: 6px;
  border-top: 1px solid #f3f4f6;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.title-popup .popup-action-footer .base-button {
  height: 32px;
  min-height: 32px;
  font-size: 0.8125rem;
  padding: 0 12px;
}

/* Table layout for popup content */
.title-popup .popup-table {
  width: 100%;
  border-collapse: collapse;
}

.title-popup .popup-table tr td {
  padding: 6px 10px;
  font-size: 0.8125rem;
  vertical-align: top;
}

.title-popup .popup-table tr td:first-child {
  color: #9ca3af;
  white-space: nowrap;
  font-weight: 500;
  padding-right: 12px;
}

.title-popup .popup-table tr td:last-child {
  color: #374151;
}

.title-popup .popup-table .popup-badge {
  display: inline-block;
  font-size: 0.6875rem;
  font-weight: 500;
  padding: 1px 6px;
  border-radius: 3px;
  margin-left: 6px;
}

.title-popup .popup-table .popup-badge--green {
  background: #dcfce7;
  color: #166534;
}

.title-popup .popup-table .popup-badge--gray {
  background: #f3f4f6;
  color: #6b7280;
}

.title-popup .popup-table .popup-badge--blue {
  background: #dbeafe;
  color: #1e40af;
}

.title-popup .popup-table .popup-badge--red {
  background: #fee2e2;
  color: #991b1b;
}
</style>
